import { template as _$template } from "solid-js/web";
import { use as _$use } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<label title="Authentic = the baked .TBL geometry. Enhanced = the optional Blender-built model (tools/enhanced), same dimensions and palette, more structure.">Compare<select>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<option>`),
  _tmpl$3 = /*#__PURE__*/_$template(`<label>LOD<select>`),
  _tmpl$4 = /*#__PURE__*/_$template(`<label title="Node index within each group — damage variant on an aircraft, animation stage on an effect (docs/formats/models-tbl.md §4.3/§4.5).">State<select>`),
  _tmpl$5 = /*#__PURE__*/_$template(`<label class=model-viewer__damage-toggle title="This object has one state: renderableMesh (packages/formats) bakes group node 0 only, and it has no sprite/sphere nodes with alternates — see docs/formats/models-tbl.md §4.5."><input type=checkbox disabled>State (single)`),
  _tmpl$6 = /*#__PURE__*/_$template(`<label title="Paint scheme: the .CLG recolour RB.EXE applies at load — the factory scheme, or a named ace's personal aircraft (docs/formats/shading-and-paint.md §4).">Scheme<select>`),
  _tmpl$7 = /*#__PURE__*/_$template(`<p class=model-viewer__message>WebGPU is not available in this browser/context (<code>navigator.gpu</code> is undefined). This port targets three.js <code>WebGPURenderer</code> only — no WebGL fallback (AD-005).`),
  _tmpl$8 = /*#__PURE__*/_$template(`<p class=model-viewer__message>Initializing WebGPU…`),
  _tmpl$9 = /*#__PURE__*/_$template(`<p class="model-viewer__message model-viewer__message--error">WebGPU failed to initialize: `),
  _tmpl$0 = /*#__PURE__*/_$template(`<div class=model-viewer__overlay>`),
  _tmpl$1 = /*#__PURE__*/_$template(`<div class=model-viewer><div class=model-viewer__toolbar><label><input type=checkbox>Wireframe</label></div><div class=model-viewer__canvas-wrap><div class=model-viewer__canvas-host>`);
import { createEffect, createResource, createSignal, For, on, onCleanup, Show } from "solid-js";
import { clampLodIndex, defaultLodIndex, lodOptions, maxLodDetail, stateCount } from "../lod-select.ts";
import { activeScheme, paintSchemes, schemePath } from "../paint-schemes.ts";
import { loadBakedModel } from "../../three/baked-model.ts";
import { loadEnhancedModel } from "../../three/enhanced-model.ts";
import { hasWebGpu } from "../../three/webgpu-support.ts";
import { enhancedIndex } from "../../three/enhanced-catalog.ts";
import { createModelScene } from "../three-model-scene.ts";
const COMPARISON_LABELS = [{
  value: "authentic",
  label: "Authentic"
}, {
  value: "enhanced",
  label: "Enhanced"
}, {
  value: "both",
  label: "Side by side"
}];

/**
 * The comparison selector, shown only for the aircraft an enhanced model has
 * actually been built for (`docs/engineering/enhanced-models.md` — one so far).
 * Everything else in the viewer is unaffected: the authentic model is what
 * loads, what the LOD and state selectors drive, and what a machine that has
 * never run `bun run enhanced` sees.
 */
function ComparisonSelect(props) {
  return _$createComponent(Show, {
    get when() {
      return props.available;
    },
    get children() {
      var _el$ = _tmpl$(),
        _el$2 = _el$.firstChild,
        _el$3 = _el$2.nextSibling;
      _el$3.addEventListener("change", event => props.onSelect(event.currentTarget.value));
      _$insert(_el$3, _$createComponent(For, {
        each: COMPARISON_LABELS,
        children: option => (() => {
          var _el$4 = _tmpl$2();
          _$insert(_el$4, () => option.label);
          _$effect(() => _el$4.selected = option.value === props.selected);
          _$effect(() => _el$4.value = option.value);
          return _el$4;
        })()
      }));
      return _el$;
    }
  });
}

/** The level-of-detail dropdown. A LOD is a whole alternative representation, so its label counts every primitive kind, not just triangles (`lod-select.ts`). */
function LodSelect(props) {
  return (() => {
    var _el$5 = _tmpl$3(),
      _el$6 = _el$5.firstChild,
      _el$7 = _el$6.nextSibling;
    _el$7.addEventListener("change", event => props.onSelect(Number(event.currentTarget.value)));
    _$insert(_el$7, _$createComponent(For, {
      get each() {
        return props.options;
      },
      children: option => (() => {
        var _el$8 = _tmpl$2();
        _$insert(_el$8, () => option.label);
        _$effect(() => _el$8.selected = option.index === clampLodIndex(props.selected, props.options.length));
        _$effect(() => _el$8.value = option.index);
        return _el$8;
      })()
    }));
    _$effect(() => _el$7.disabled = props.options.length === 0);
    return _el$5;
  })();
}

/** The damage/animation state dropdown, replaced by a disabled placeholder for the objects that only have one state. */
function StateSelect(props) {
  return _$createComponent(Show, {
    get when() {
      return props.count > 1;
    },
    get fallback() {
      return (() => {
        var _el$10 = _tmpl$5(),
          _el$11 = _el$10.firstChild;
        _el$11.checked = false;
        return _el$10;
      })();
    },
    get children() {
      var _el$9 = _tmpl$4(),
        _el$0 = _el$9.firstChild,
        _el$1 = _el$0.nextSibling;
      _el$1.addEventListener("change", event => props.onSelect(Number(event.currentTarget.value)));
      _$insert(_el$1, _$createComponent(For, {
        get each() {
          return Array.from({
            length: props.count
          }, (_, index) => index);
        },
        children: index => (() => {
          var _el$12 = _tmpl$2();
          _el$12.value = index;
          _$insert(_el$12, index);
          _$effect(() => _el$12.selected = index === props.selected);
          return _el$12;
        })()
      }));
      return _el$9;
    }
  });
}

/** The paint-scheme dropdown, shown only for the 22 fighters — every other model has a single scheme (i.e. none). */
function SchemeSelect(props) {
  return _$createComponent(Show, {
    get when() {
      return props.schemes.length > 1;
    },
    get children() {
      var _el$13 = _tmpl$6(),
        _el$14 = _el$13.firstChild,
        _el$15 = _el$14.nextSibling;
      _el$15.addEventListener("change", event => props.onSelect(event.currentTarget.value));
      _$insert(_el$15, _$createComponent(For, {
        get each() {
          return props.schemes;
        },
        children: option => (() => {
          var _el$16 = _tmpl$2();
          _$insert(_el$16, () => option.label);
          _$effect(() => _el$16.selected = option.label === props.selected);
          _$effect(() => _el$16.value = option.label);
          return _el$16;
        })()
      }));
      return _el$13;
    }
  });
}

/**
 * Interactive three.js WebGPU viewer for one baked model object (all its
 * LODs). Flat-shaded, palette-coloured, untextured — matches the source
 * data (docs/formats/models-tbl.md, packages/baker/src/model-bake.ts) via
 * a non-indexed `BufferGeometry` with per-vertex flat colour/normal
 * (`model-geometry.ts`, no GPU needed to unit-test). The three.js/WebGPU
 * scene lifecycle itself lives in `three-model-scene.ts`, kept out of this
 * component so this file stays JSX-and-wiring only.
 *
 * No WebGL fallback (AD-005): if `navigator.gpu` is absent, this shows a
 * message and never touches the WebGPU/three.js renderer APIs at all — the
 * whole point of feature-detecting up front is that headless test contexts
 * without WebGPU still render a clean, no-console-error pane.
 *
 * A LOD is not only triangles. 55 of the corpus's 360 objects have none at
 * any LOD — `TERR*`'s roads and rivers are 2-point faces, and
 * `FLAK`/`EXPLO`/`SMOKE`/`MISC#19` are sprite billboards
 * (docs/formats/models-tbl.md §4.3) — so the LOD dropdown labels every
 * primitive kind, not just the triangle count that used to read "0 tri" for
 * all of them.
 *
 * State selector: the engine draws node `stateArray[group.stateChannel]` of
 * each group (§4.5, verified against `RB.EXE`'s group walk). For an aircraft
 * that picks the damage variant; for an effect it is the animation cursor —
 * `FLAK`'s four nodes are four stages of one burst. The mesh half of the
 * bake still only carries node 0 (`renderableMesh`), so the selector moves
 * sprites and spheres and leaves triangles alone; it is hidden entirely for
 * the models that have only one state.
 *
 * Scheme selector: a fighter is baked once per paint scheme
 * (docs/formats/shading-and-paint.md §4) — the factory colours plus every
 * ace who flies the type — so switching scheme swaps which baked model is
 * fetched, not how it is drawn. Hidden for the models the engine never
 * repaints, which is everything but the 22 fighters.
 */
export function ModelViewer(props) {
  const [scheme, setScheme] = createSignal(activeScheme(props.entry));
  const schemes = () => paintSchemes(props.entry);
  const [modelData] = createResource(() => schemePath(props.entry, scheme()), loadBakedModel);
  const [lodIndex, setLodIndex] = createSignal(0);
  const [stateIndex, setStateIndex] = createSignal(0);
  const [wireframe, setWireframe] = createSignal(false);
  const [comparison, setComparison] = createSignal("authentic");
  const enhancedId = () => schemePath(props.entry, scheme()).replace(/\.json$/, "");
  const enhancedPath = () => enhancedIndex()?.get(enhancedId())?.path;
  // The scene *owns and disposes* the enhanced model, so the resource must not
  // hand the same object to two scenes. Keying on the scene's generation as
  // well as the path guarantees a fresh load per scene: without it, selecting
  // an entry whose enhanced path matches the one already showing would re-add
  // an object whose geometry had just been freed.
  const [sceneEpoch, setSceneEpoch] = createSignal(0);
  const [enhancedModel] = createResource(() => {
    const path = enhancedPath();
    return path === undefined ? undefined : {
      path,
      epoch: sceneEpoch()
    };
  }, source => loadEnhancedModel(source.path));
  const [status, setStatus] = createSignal(hasWebGpu() ? "initializing" : "unavailable");
  const [errorMessage, setErrorMessage] = createSignal(undefined);
  let containerRef;
  let scene;
  function teardownScene() {
    scene?.dispose();
    scene = undefined;
  }
  onCleanup(teardownScene);

  // (Re)creates the whole three.js scene whenever the selected asset
  // changes. Simplest-correct: tear down completely and start over rather
  // than diffing what changed between two arbitrary models.
  createEffect(() => {
    void props.entry.path; // track: a new asset selection re-initializes the scene
    teardownScene();
    if (!hasWebGpu() || !containerRef) {
      setStatus(hasWebGpu() ? "initializing" : "unavailable");
      return;
    }
    setStatus("initializing");
    setErrorMessage(undefined);
    setSceneEpoch(epoch => epoch + 1);
    scene = createModelScene(containerRef, {
      onStatus: (nextStatus, message) => {
        setStatus(nextStatus);
        setErrorMessage(message);
      }
    });
  });

  // A new asset selection starts on the scheme that entry itself was baked
  // in — opening `models/FOKDR1@Richthofen` shows Richthofen's, not the
  // factory one, however the previous selection was left.
  createEffect(on(() => props.entry.id, () => setScheme(activeScheme(props.entry))));

  // Default the LOD selector to the most detailed LOD every time a *new*
  // model's data arrives (not on every unrelated re-render).
  createEffect(on(modelData, data => {
    if (data) {
      setLodIndex(defaultLodIndex(data.lods.length));
      setStateIndex(0);
    }
  }));

  // Push the selected LOD into the scene whenever the loaded data, the scene, or either selection changes.
  createEffect(() => {
    const data = modelData();
    if (status() !== "ready" || !data || !scene) return;
    const lod = data.lods[clampLodIndex(lodIndex(), data.lods.length)];
    if (lod) scene.setLod(lod, {
      boundingRadius: data.boundingRadius,
      maxDetail: maxLodDetail(data.lods),
      spriteSheet: data.spriteSheet,
      stateIndex: stateIndex()
    });
  });

  // Wireframe toggle: flip the live mesh's material, no geometry rebuild / camera reset.
  createEffect(() => scene?.setWireframe(wireframe()));

  // The enhanced model is handed to the scene, which owns and disposes it. A
  // scheme change swaps it for the ace's own, exactly as it swaps the baked one.
  createEffect(() => {
    const model = enhancedModel();
    if (status() !== "ready" || !scene) return;
    scene.setEnhanced(model);
  });

  // An aircraft with no enhanced model must not stay stuck showing nothing.
  createEffect(() => {
    if (status() !== "ready" || !scene) return;
    scene.setComparison(enhancedPath() ? comparison() : "authentic");
  });
  const lods = () => lodOptions(modelData()?.lods ?? []);
  const states = () => stateCount(modelData()?.lods ?? []);
  return (() => {
    var _el$17 = _tmpl$1(),
      _el$18 = _el$17.firstChild,
      _el$19 = _el$18.firstChild,
      _el$20 = _el$19.firstChild,
      _el$21 = _el$18.nextSibling,
      _el$22 = _el$21.firstChild;
    _$insert(_el$18, _$createComponent(LodSelect, {
      get options() {
        return lods();
      },
      get selected() {
        return lodIndex();
      },
      onSelect: setLodIndex
    }), _el$19);
    _$insert(_el$18, _$createComponent(SchemeSelect, {
      get schemes() {
        return schemes();
      },
      get selected() {
        return scheme();
      },
      onSelect: setScheme
    }), _el$19);
    _$insert(_el$18, _$createComponent(ComparisonSelect, {
      get available() {
        return enhancedPath() !== undefined;
      },
      get selected() {
        return comparison();
      },
      onSelect: setComparison
    }), _el$19);
    _el$20.addEventListener("change", event => setWireframe(event.currentTarget.checked));
    _$insert(_el$18, _$createComponent(StateSelect, {
      get count() {
        return states();
      },
      get selected() {
        return stateIndex();
      },
      onSelect: setStateIndex
    }), null);
    var _ref$ = containerRef;
    typeof _ref$ === "function" ? _$use(_ref$, _el$22) : containerRef = _el$22;
    _$insert(_el$21, _$createComponent(Show, {
      get when() {
        return status() !== "ready";
      },
      get children() {
        var _el$23 = _tmpl$0();
        _$insert(_el$23, _$createComponent(Show, {
          get when() {
            return status() === "unavailable";
          },
          get children() {
            return _tmpl$7();
          }
        }), null);
        _$insert(_el$23, _$createComponent(Show, {
          get when() {
            return status() === "initializing";
          },
          get children() {
            return _tmpl$8();
          }
        }), null);
        _$insert(_el$23, _$createComponent(Show, {
          get when() {
            return status() === "error";
          },
          get children() {
            var _el$26 = _tmpl$9(),
              _el$27 = _el$26.firstChild;
            _$insert(_el$26, errorMessage, null);
            return _el$26;
          }
        }), null);
        return _el$23;
      }
    }), null);
    _$effect(() => _el$20.disabled = status() !== "ready");
    _$effect(() => _el$20.checked = wireframe());
    return _el$17;
  })();
}